import { SignUpButton, useAuth } from "@clerk/clerk-react";
import useSubscriptionInfo from "@/hooks/useSubscriptionInfo";
import createCheckoutSession from "@/lib/createCheckoutSession";
import { PLANS } from "@/constants/plans";
import { CheckCircle2, Zap, Star, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PLAN_META = {
  free:         { icon: Star,     label: "Get started" },
  standard:     { icon: Zap,      label: "Get Standard" },
  professional: { icon: Sparkles, label: "Get Professional" },
};

export default function Pricing() {
  const { planName } = useSubscriptionInfo();
  const { getToken } = useAuth();

  const handleUpgrade = async (priceId) => {
    const token = await getToken();
    await createCheckoutSession(priceId, token);
  };

  return (
    <div className="pricing-page">

      {/* ── Header ──────────────────────────────────────────────── */}
      <section className="section-base">
        <div className="section-inner pricing-header">
          <div className="section-label">Plans &amp; Pricing</div>
          <h1 className="section-heading" style={{ marginBottom: "1rem" }}>
            Simple pricing for every classroom.
          </h1>
          <p className="pricing-subhead">
            Start free — no credit card required. Upgrade when you need more
            reports or AI generations. All plans reset every 30 days.
          </p>
        </div>
      </section>

      {/* ── Cards ───────────────────────────────────────────────── */}
      <section className="section-alt">
        <div className="section-inner">
          <div className="pricing-grid">
            {PLANS.map((plan) => {
              const meta  = PLAN_META[plan.planKey];
              const Icon  = meta.icon;
              const isCurrent = planName === plan.planKey;

              return (
                <div
                  key={plan.planKey}
                  className={`pricing-card${plan.highlight ? " featured" : ""}`}
                >
                  {plan.highlight && (
                    <div className="pricing-popular">Most popular</div>
                  )}

                  <div className="pricing-card-top">
                    <div className="pricing-card-icon">
                      <Icon size={18} />
                    </div>
                    <div className="pricing-card-name">{plan.name}</div>
                    <div className="pricing-card-price">
                      {plan.price}
                      <span>{plan.period}</span>
                    </div>
                  </div>

                  <ul className="pricing-features">
                    {plan.features.map((f) => (
                      <li key={f}>
                        <CheckCircle2 size={14} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="pricing-card-footer">
                    {isCurrent ? (
                      <div className="pricing-current-badge">
                        <CheckCircle2 size={15} />
                        Current plan
                      </div>
                    ) : plan.stripePlanKey ? (
                      <button
                        className={plan.highlight ? "cta-primary pricing-btn" : "cta-secondary pricing-btn"}
                        onClick={() => handleUpgrade(plan.stripePlanKey)}
                      >
                        {meta.label} <ArrowRight size={14} />
                      </button>
                    ) : (
                      <SignUpButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                        <button className="cta-secondary pricing-btn">
                          {meta.label} <ArrowRight size={14} />
                        </button>
                      </SignUpButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ strip ───────────────────────────────────────────── */}
      <section className="section-base">
        <div className="section-inner pricing-faq">
          <div className="section-label">Common questions</div>
          <h2 className="section-heading" style={{ marginBottom: "2rem" }}>
            Good to know before you sign up.
          </h2>
          <div className="faq-grid">
            {FAQ.map((item) => (
              <div key={item.q} className="faq-item">
                <h3 className="faq-q">{item.q}</h3>
                <p className="faq-a">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ──────────────────────────────────────────── */}
      <section className="section-dark">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <div className="section-label light">Get started today</div>
          <h2 className="section-heading light">
            Try it free — no account needed.
          </h2>
          <div className="hero-ctas" style={{ justifyContent: "center" }}>
            <Link to="/pgrc">
              <button className="cta-primary">
                Try a free report card <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}

const FAQ = [
  {
    q: "What counts as a 'report card'?",
    a: "Each student report you create counts as one. Editing and saving an existing report does not use an additional slot.",
  },
  {
    q: "When does my usage reset?",
    a: "Usage counters reset every 30 days from the date your plan started — not on a calendar month boundary.",
  },
  {
    q: "Can I share a class with a colleague?",
    a: "Yes. You can share any class with any EasyReports user and give them view or edit access. Shared classes count against the owner's quota.",
  },
  {
    q: "What happens when my paid plan expires?",
    a: "Your plan expires after one year. If you don't renew, your account automatically drops to the free tier limits. Your data stays intact.",
  },
  {
    q: "Is there a free trial for paid plans?",
    a: "The Free plan is a permanent free tier — 5 reports and 10 AI generations every 30 days. No trial period needed; upgrade only when you outgrow it.",
  },
  {
    q: "What report card types are supported?",
    a: "Ontario Progress Reports (Grades 1–6) and Provincial Report Cards (Grades 1–8) are fully supported, with more types in development.",
  },
];
